/**
 * Anzeige am Matchende (Masterprompt Abschnitte 89, 90).
 *
 * Seit v0.20 mit Zahlen zum Match (Abschnitt 92): beide Spalten
 * nebeneinander, damit man sieht, woran es lag. Im Match bleibt Gold geheim -
 * danach hat Geheimhaltung keinen Zweck mehr (Produktentscheidung vom
 * 25.09.2026).
 *
 * Fuer die naechste Runde stehen hier dieselben Bedienelemente wie im
 * Warteraum: "Ready" und die festen Kurznachrichten (Produktentscheidung vom
 * 05.10.2026). Vorher gab es dafuer eine eigene zweite "beide bereit?"-
 * Abfrage - zwei Mechaniken fuer dieselbe Frage waren eine zu viel.
 *
 * Wer lieber einen anderen Gegner sucht, geht zurueck ins Menue.
 */

import { getHero } from '@dueline/game-data';
import type { MatchStatsPayload } from '@dueline/shared';
import { t } from '../i18n/index.js';
import type { MatchSnapshot } from '../net/MatchConnection.js';
import type { LOBBY_SIGNALS } from '@dueline/shared';
import { LobbyControls, LobbySignalLine } from './LobbyControls.js';
import { formatZahl } from './economyView.js';

/**
 * Zusatz hinter der Matchdauer: warum es vorbei ist.
 *
 * Beim Aufgeben steht ausdruecklich da, WER aufgegeben hat - sonst liest es
 * sich, als haette der Gegner den Stecker gezogen.
 */
function grundText(snapshot: MatchSnapshot): string {
  if (snapshot.matchEndReason === 'surrender') {
    return snapshot.outcome === 'defeat'
      ? ` - ${t.matchEnd.bySurrenderOwn}`
      : ` - ${t.matchEnd.bySurrenderOpponent}`;
  }
  if (snapshot.matchEndReason === 'abandoned') return ` - ${t.matchEnd.byAbandon}`;
  return '';
}

function formatTime(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return `${minutes}:${rest.toString().padStart(2, '0')}`;
}

export function MatchEndOverlay({
  snapshot,
  onToggleReady,
  onSignal,
  onLeave,
}: {
  snapshot: MatchSnapshot;
  onToggleReady: () => void;
  onSignal: (signal: (typeof LOBBY_SIGNALS)[number]) => void;
  /** Zurueck ins Hauptmenue - statt die Seite neu zu laden. */
  onLeave: () => void;
}) {
  if (!snapshot.outcome) return null;

  const sieg = snapshot.outcome === 'victory';
  const gegner = snapshot.opponent;

  return (
    <div className="dl-status dl-status--result">
      <h2 className={`dl-result__headline dl-result__headline--${sieg ? 'victory' : 'defeat'}`}>
        {sieg ? t.matchEnd.victory : t.matchEnd.defeat}
      </h2>
      <p className="dl-status__detail">{sieg ? t.matchEnd.victoryNote : t.matchEnd.defeatNote}</p>
      <p className="dl-status__detail">
        {t.matchEnd.duration}: {formatTime(snapshot.matchDurationSeconds)}
        {grundText(snapshot)}
      </p>

      <MatchStats own={snapshot.ownStats} opponent={snapshot.opponentStats} />

      {/* Ohne Gegner gibt es nichts zu wiederholen. */}
      {gegner ? (
        <>
          <LobbyControls snapshot={snapshot} onToggleReady={onToggleReady} onSignal={onSignal} />

          <LobbySignalLine snapshot={snapshot} />

          <button type="button" className="dl-button dl-lobby__leave" onClick={onLeave}>
            {t.matchEnd.reload}
          </button>

          <p className="dl-status__detail dl-status__detail--small">{t.matchEnd.reloadHint}</p>
        </>
      ) : (
        <>
          <p className="dl-status__detail">{t.matchEnd.rematchNoOpponent}</p>
          <button type="button" className="dl-button" onClick={onLeave}>
            {t.matchEnd.reload}
          </button>
        </>
      )}
    </div>
  );
}

/**
 * Die Zahlen des Matches, beide Spalten nebeneinander.
 *
 * Bewusst eine Auswahl: Der Server hebt deutlich mehr auf (fuer spaetere
 * Auswertungen), hier steht nur, was nach einem Match wirklich interessiert.
 */
function MatchStats({
  own,
  opponent,
}: {
  own: MatchStatsPayload | null;
  opponent: MatchStatsPayload | null;
}) {
  if (!own) return null;

  const zeilen: { label: string; eigen: string; fremd: string }[] = [
    zeile(t.matchEnd.stats.portalHp, own, opponent, (s) => s.portalHp),
    zeile(t.matchEnd.stats.creepsSent, own, opponent, (s) => s.creepsSent),
    zeile(t.matchEnd.stats.wavesSent, own, opponent, (s) => s.wavesSent),
    zeile(t.matchEnd.stats.kills, own, opponent, (s) => s.killsByHero),
    zeile(t.matchEnd.stats.income, own, opponent, (s) => s.income),
    zeile(t.matchEnd.stats.goldEarned, own, opponent, (s) => s.goldEarned),
    zeile(t.matchEnd.stats.goldSpent, own, opponent, (s) => s.goldSpent),
    {
      label: t.matchEnd.stats.tier,
      eigen: tierText(own),
      fremd: opponent ? tierText(opponent) : '-',
    },
    zeile(t.matchEnd.stats.level, own, opponent, (s) => s.level),
  ];

  return (
    <div className="dl-result__stats">
      <div className="dl-result__statsHead">
        <span className="dl-result__statsTitle">{t.matchEnd.statsTitle}</span>
        <span>{heldName(own)}</span>
        <span>{opponent ? heldName(opponent) : '-'}</span>
      </div>
      {zeilen.map((eintrag) => (
        <div key={eintrag.label} className="dl-result__statsRow">
          <span className="dl-result__statsLabel">{eintrag.label}</span>
          <span>{eintrag.eigen}</span>
          <span>{eintrag.fremd}</span>
        </div>
      ))}
    </div>
  );
}

/** Eine Zeile mit derselben Zahl fuer beide Seiten. */
function zeile(
  label: string,
  own: MatchStatsPayload,
  opponent: MatchStatsPayload | null,
  lies: (stats: MatchStatsPayload) => number,
): { label: string; eigen: string; fremd: string } {
  /*
   * Englische Schreibweise wie ueberall sonst (16.10.2026).
   *
   * Hier stand die deutsche: "Gold earned 1.632" mit Punkt, mitten in
   * einer englischen Oberflaeche. formatZahl ist jetzt die einzige Stelle
   * im Client, die Zahlen trennt.
   */
  return {
    label,
    eigen: formatZahl(lies(own)),
    fremd: opponent ? formatZahl(lies(opponent)) : '-',
  };
}

/** Hoechstes Tier, bei Bedarf mit dem Zeitpunkt. */
function tierText(stats: MatchStatsPayload): string {
  const roemisch = ['I', 'II', 'III', 'IV'][Math.max(0, stats.highestTier - 1)] ?? 'I';
  if (stats.highestTierAtSeconds === null) return roemisch;
  return `${roemisch} (${formatTime(stats.highestTierAtSeconds)})`;
}

/** Name des Heroes - beim Testgegner mit Hinweis. */
function heldName(stats: MatchStatsPayload): string {
  const hero = getHero(stats.heroId)?.name ?? stats.heroId;
  return stats.isBot ? `${hero} (${t.matchEnd.bot})` : hero;
}
